import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/employee-redux";
import { uniq, flatten, values } from "lodash";
import { Container, Loader, Message } from "semantic-ui-react";

import { ManagerFeedbackOnReportQuestionnaire } from "@teamprevent/common/managerFeedbackOnReport/components/ManagerFeedbackOnReportQuestionnaire";
import {
    ManagerFeedbackOnReportQuestionnaireModel,
    MANAGER_FEEDBACK_ON_REPORT_KEY,
    ParentConsultationModel
} from "@teamprevent/common/managerFeedbackOnReport/models";
import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { toast } from "@teamprevent/common/toasts";

import { ApplicationState } from "../../applicationState";
import { loadOhCaseDetail } from "../../crud/ohCase/actions";
import { submitManagerFeedbackOnReportQuestionnaire } from "../actions";
import { managerFeedbackOnReportApi } from "../managerFeedbackOnReportApi";

import { push } from "redux-little-router";

export const ManagerFeedbackOnReportForm: React.FunctionComponent = () => {
    const dispatch = useDispatch();

    const [parentConsultation, setParentConsultation] = React.useState<ParentConsultationModel>(null);

    const ohCaseId = useSelector((state: ApplicationState) => state.router.params.ohCaseId);
    const taskId = useSelector((state: ApplicationState) => state.router.params.taskId);

    const ohCase = useSelector((state: ApplicationState) => state.ohCases?.find(c => c.id === ohCaseId));
    const task = ohCase?.tasks?.find(t => t.id === taskId);

    const stateQuestionnaire = useSelector((state: ApplicationState) => state.questionnaires[MANAGER_FEEDBACK_ON_REPORT_KEY]);
    const questions: AnsweredQuestionWithId[] = uniq(flatten(stateQuestionnaire?.sections?.map(section => values(section.questions))));
    const eTag = stateQuestionnaire?.eTag;
    const feedbackQuestionnaire: ManagerFeedbackOnReportQuestionnaireModel = { questions, eTag };
    const isQuestionnaireValid = questions?.every(s => s.isValid === true);

    React.useEffect(() => {
        if (ohCaseId && !ohCase) {
            dispatch(loadOhCaseDetail({ ohCaseId }));
        }
    }, [ohCaseId]);

    React.useEffect(() => {
        (async () => {
            if (ohCaseId && taskId) {
                const data = await managerFeedbackOnReportApi.getConsultationData(ohCaseId, taskId);
                setParentConsultation(data);
            }
        })();
    }, [ohCaseId, taskId]);

    const goToOhCaseDetails = () => dispatch(push(`/ohCases/${ohCaseId}`));

    const submit = async (): Promise<boolean> => {
        if (isQuestionnaireValid) {
            await dispatch(submitManagerFeedbackOnReportQuestionnaire(ohCaseId, taskId, questions));
            goToOhCaseDetails();

            return true;
        } else {
            toast.warning("Questionnaire is invalid - please fill in all of the mandatory fields");
            return false;
        }
    };

    if (task?.completed) {
        return (
            <Container className="referral-report">
                <Message><strong>The survey has already been completed. Thank you for your feedback!</strong></Message>
            </Container>
        );
    }

    if (ohCase && task && parentConsultation) {
        return (
            <ManagerFeedbackOnReportQuestionnaire
                customerId={null}
                ohCase={ohCase}
                task={task}
                parentConsultation={parentConsultation}
                submitQuestionnaire={submit}
                questionnaire={feedbackQuestionnaire}
            />
        );
    }

    return <Loader active />;
};
